/**
 * Documentation Service
 *
 * REST client for the AI-powered documentation generation API.
 * Uses the shared axiosInstance (with auth token injection) but overrides
 * the baseURL to point at the docs microservice.
 *
 * API:
 *   GET  /docs/{file_id} — the document, if generated; no LLM, no spend
 *   POST /docs/generate  — generate it
 */

import { createAxiosInstance } from '@/dataProvider/axios';
import { env } from '@/config/env';
import type {
  GenerateDocumentationInput,
  GenerateDocumentationResponse,
} from './types';

export * from './types';

// ============================================================================
// Axios instance for the docs microservice
// Routed through backend proxy which injects GCP OIDC token
// ============================================================================

/**
 * Dedicated axios instance for the documentation microservice.
 * Inherits JWT auth token injection from createAxiosInstance.
 */
const docsAxiosInstance = createAxiosInstance(`${env.API_URL}/proxy/graph`);

// ============================================================================
// Service functions
// ============================================================================

/**
 * Generate AI documentation for a Salesforce metadata component.
 *
 * @param input   - Request payload (org_id, file_id, class_name, metadata_type, …)
 * @param orgId   - Organization UUID — sent as X-Organization-ID header
 * @param userId  - User ID — sent as X-User-ID header (optional)
 * @returns Generated documentation response
 */
export async function generateDocumentation(
  input: GenerateDocumentationInput,
  orgId: string,
  userId?: string,
): Promise<GenerateDocumentationResponse> {
  const response = await docsAxiosInstance.post<GenerateDocumentationResponse>(
    '/docs/generate',
    {
      org_id: input.org_id,
      file_id: input.file_id,
      class_name: input.class_name,
      metadata_type: input.metadata_type,
      user_id: userId ?? input.user_id ?? '',
      enrich: input.enrich ?? true,
    },
    {
      headers: {
        'X-Organization-ID': orgId,
        'X-User-ID': userId ?? '',
      },
    },
  );

  return response.data;
}

/**
 * The document, or null if it has not been generated yet.
 *
 * Reading and generating are separate operations on the service side
 * (`services/doc_reader.py`), so they are separate calls here too. Reading costs
 * nothing and is a GET, which is why it survives the backend proxy's budget gate;
 * generating may spend a seat's budget and is correctly blocked when over budget.
 *
 * Returns null on 404 (`doc_not_generated`) so the caller can decide whether to
 * generate. Every other error propagates: "the server is broken" must not look like
 * "not generated yet" and silently trigger paid work.
 */
export async function getDocumentation(
  input: GenerateDocumentationInput,
  orgId: string,
  userId?: string,
): Promise<GenerateDocumentationResponse | null> {
  try {
    const response = await docsAxiosInstance.get<GenerateDocumentationResponse>(
      `/docs/${encodeURIComponent(input.file_id)}`,
      {
        params: {
          org_id: input.org_id,
          class_name: input.class_name,
          metadata_type: input.metadata_type,
        },
        headers: {
          'X-Organization-ID': orgId,
          'X-User-ID': userId ?? '',
        },
        // A 404 is an expected answer here, not a failure worth logging on every first open.
        silent: true,
      },
    );
    return response.data;
  } catch (error) {
    if ((error as { response?: { status?: number } })?.response?.status === 404) return null;
    throw error;
  }
}
